import type { PipelineSummary } from "@/features/board/api/use-board";
import type { ComponentScreen } from "@/features/component-views/api/use-component-view";
import type { EntityDefinition } from "@/shared/contracts/structure";
import type { NavRow } from "./nav-items";

/** Quantos funis o menu desenha; o resto fica a um clique, em Funis. */
export const FUNNEL_LIMIT = 8;

/**
 * A PARTE DO MENU QUE O TENANT ESCREVEU — uma linha por dado.
 *
 * O quadro, os calendários e as telas sob medida de um dado são VISTAS dele (o "Ver
 * como" da lista), nunca outra linha: a linha fica marcada em qualquer uma delas. Só
 * sobra linha própria para o que não tem dado no menu — a tela ou o funil de um dado
 * escondido —, e cai no grupo desse dado.
 *
 * Fora daqui: o dado sem `showInNav` (o construtor o acha em /system) e o que o
 * documento mandou para Configurações → Cadastros (`inSettings`).
 *
 * A linha abre a tela sob medida que o documento marcou como a principal do dado
 * (`isDefault`), com a tabela a um clique no "Ver como"; sem marca, abre a tabela.
 *
 * A ordem é a que o documento declarou (`navOrder`, gravado na criação), e um grupo
 * aparece onde aparece o primeiro dado dele.
 */
export function entityMenu(input: {
	readonly entities: readonly EntityDefinition[];
	readonly pipelines: readonly PipelineSummary[];
	readonly screens: readonly ComponentScreen[];
	readonly path: string;
	readonly funnel: unknown;
	readonly noGroup: string;
}): { groups: [string, NavRow[]][]; funnels: NavRow[] } {
	const { entities, pipelines, path, funnel } = input;
	const listed = entities
		.filter((e) => e.showInNav && e.status === "active" && !e.inSettings)
		.sort((a, b) => a.navOrder - b.navOrder || a.name.localeCompare(b.name));
	const inMenu = new Set(listed.map((e) => e.code));
	const groupOf = (code: string) =>
		entities.find((e) => e.code === code)?.navGroup ?? input.noGroup;

	const groups = new Map<string, NavRow[]>();
	const add = (key: string, row: NavRow) => groups.set(key, [...(groups.get(key) ?? []), row]);
	for (const entity of listed) {
		const code = entity.code;
		const onBoard =
			path === "/board" && pipelines.some((p) => p.entityCode === code && p.code === funnel);
		const main = input.screens.find((s) => s.entityCode === code && s.isDefault);
		add(entity.navGroup ?? input.noGroup, {
			to: main ? `/t/${code}/${main.code}` : `/e/${code}`,
			label: entity.name,
			initials: entity.name.slice(0, 2),
			active:
				path === `/e/${code}` ||
				path.startsWith(`/e/${code}/`) ||
				path.startsWith(`/t/${code}/`) ||
				onBoard,
		});
	}
	for (const screen of input.screens) {
		if (inMenu.has(screen.entityCode)) continue;
		add(groupOf(screen.entityCode), {
			to: `/t/${screen.entityCode}/${screen.code}`,
			label: screen.name,
			initials: screen.name.slice(0, 2),
		});
	}

	const funnels = pipelines
		.filter((p) => !inMenu.has(p.entityCode))
		.slice(0, FUNNEL_LIMIT)
		.map<NavRow>((p) => ({
			to: `/board?funil=${p.code}`,
			label: p.name,
			icon: "funnel",
			active: path === "/board" && funnel === p.code,
		}));
	return { groups: [...groups.entries()], funnels };
}
